// apps/client/src/contexts/QueueContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { JobStatus, QueueType } from '@fsp/types';
import { useQueryClient } from '@tanstack/react-query';
import { chapterKeys } from '../hooks/queries/useChapters';

// ============================================
// QUEUE TYPES
// ============================================

// Page Generation Job
export interface PageGenerationJob {
  queueType: QueueType.PAGE_GENERATION;
  jobId: string;
  pageId: string;
  pageTitle: string;
  chapterTitle: string;
  status: JobStatus;
  errorMessage?: string;
  organisationId: string;
  planId: string;
  chapterId: string;
  pollInterval?: number;
}

// Plan Import Job (for future use)
export interface PlanImportJob {
  queueType: QueueType.PLAN_IMPORT;
  jobId: string;
  status: JobStatus;
  progress: number;
  total: number;
  errorMessage?: string;
  organisationId: string;
  planId?: string;
  pollInterval?: number;
}

// Flowchart Generation Job
export interface FlowchartGenerationJob {
  queueType: QueueType.FLOWCHART_GENERATION;
  jobId: string;
  planId: string;
  status: JobStatus;
  errorMessage?: string;
  organisationId: string;
  pollInterval?: number;
  initialFlowchartTimestamp?: string; // Track when the job started to detect updates
  pollCount?: number; // Track how many times we've polled
}

export type QueueJob = PageGenerationJob | PlanImportJob | FlowchartGenerationJob;

// ============================================
// CONTEXT INTERFACE
// ============================================

interface QueueContextType {
  // Generic queue management
  jobs: Map<string, QueueJob>;
  addJob: (job: QueueJob) => void;
  updateJob: (jobId: string, updates: Partial<QueueJob>) => void;
  removeJob: (jobId: string) => void;

  // Convenience getters
  pageGenerationJobs: PageGenerationJob[];
  planImportJob: PlanImportJob | null;
  flowchartGenerationJobs: FlowchartGenerationJob[];

  // Page Generation specific
  startPageGeneration: (job: Omit<PageGenerationJob, 'queueType'>) => void;
  completePageGeneration: (jobId: string, success: boolean, errorMessage?: string) => void;
  clearPageGeneration: (jobId: string) => void;

  // Flowchart Generation specific
  startFlowchartGeneration: (job: Omit<FlowchartGenerationJob, 'queueType'>) => void;
  completeFlowchartGeneration: (jobId: string, success: boolean, errorMessage?: string) => void;
  clearFlowchartGeneration: (jobId: string) => void;
}

const QueueContext = createContext<QueueContextType | undefined>(undefined);

export const useQueue = () => {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useQueue must be used within a QueueProvider');
  }
  return context;
};

// Convenience hook for page generation
export const usePageGeneration = () => {
  const context = useQueue();
  return {
    jobs: context.pageGenerationJobs,
    startGeneration: context.startPageGeneration,
    completeGeneration: context.completePageGeneration,
    clearGeneration: context.clearPageGeneration,
  };
};

// ============================================
// PROVIDER
// ============================================

interface QueueProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'queueJobs';

export const QueueProvider: React.FC<QueueProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Map<string, QueueJob>>(new Map());
  const [initialized, setInitialized] = useState(false);
  const pollIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const queryClient = useQueryClient();

  // Persist jobs to sessionStorage
  const persistJobs = useCallback((jobsMap: Map<string, QueueJob>) => {
    const jobsArray = Array.from(jobsMap.values());
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobsArray));
  }, []);

  // Generic job management
  const addJob = useCallback((job: QueueJob) => {
    setJobs((prev) => {
      const next = new Map(prev);
      next.set(job.jobId, job);
      persistJobs(next);
      return next;
    });
  }, [persistJobs]);

  const updateJob = useCallback((jobId: string, updates: Partial<QueueJob>) => {
    setJobs((prev) => {
      const job = prev.get(jobId);
      if (!job) return prev;

      const next = new Map(prev);
      next.set(jobId, { ...job, ...updates } as QueueJob);
      persistJobs(next);
      return next;
    });
  }, [persistJobs]);

  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => {
      const next = new Map(prev);
      next.delete(jobId);
      persistJobs(next);
      return next;
    });

    // Clear polling interval
    const interval = pollIntervalsRef.current.get(jobId);
    if (interval) {
      clearInterval(interval);
      pollIntervalsRef.current.delete(jobId);
    }
  }, [persistJobs]);

  // Convenience getters
  const pageGenerationJobs = Array.from(jobs.values()).filter(
    (j): j is PageGenerationJob => j.queueType === QueueType.PAGE_GENERATION
  );

  const planImportJob = Array.from(jobs.values()).find(
    (j): j is PlanImportJob => j.queueType === QueueType.PLAN_IMPORT
  ) || null;

  const flowchartGenerationJobs = Array.from(jobs.values()).filter(
    (j): j is FlowchartGenerationJob => j.queueType === QueueType.FLOWCHART_GENERATION
  );

  // ============================================
  // PAGE GENERATION SPECIFIC
  // ============================================

  const startPageGeneration = useCallback((job: Omit<PageGenerationJob, 'queueType'>) => {
    addJob({ ...job, queueType: QueueType.PAGE_GENERATION });
  }, [addJob]);

  const completePageGeneration = useCallback((
    jobId: string,
    success: boolean,
    errorMessage?: string
  ) => {
    const job = jobs.get(jobId) as PageGenerationJob | undefined;

    updateJob(jobId, {
      status: success ? JobStatus.COMPLETED : JobStatus.FAILED,
      errorMessage,
    } as Partial<PageGenerationJob>);

    // Invalidate chapter query to update PagesList
    if (job) {
      queryClient.invalidateQueries({
        queryKey: chapterKeys.detail(job.organisationId, job.planId, job.chapterId),
      });
    }

    // Auto-clear after 5 seconds
    setTimeout(() => {
      removeJob(jobId);
    }, 5000);
  }, [updateJob, removeJob, jobs, queryClient]);

  const clearPageGeneration = useCallback((jobId: string) => {
    removeJob(jobId);
  }, [removeJob]);

  // ============================================
  // FLOWCHART GENERATION SPECIFIC
  // ============================================

  const startFlowchartGeneration = useCallback((job: Omit<FlowchartGenerationJob, 'queueType'>) => {
    addJob({ ...job, queueType: QueueType.FLOWCHART_GENERATION });
  }, [addJob]);

  const completeFlowchartGeneration = useCallback((
    jobId: string,
    success: boolean,
    errorMessage?: string
  ) => {
    updateJob(jobId, {
      status: success ? JobStatus.COMPLETED : JobStatus.FAILED,
      errorMessage,
    } as Partial<FlowchartGenerationJob>);

    // Auto-clear after 5 seconds
    setTimeout(() => {
      removeJob(jobId);
    }, 5000);
  }, [updateJob, removeJob]);

  const clearFlowchartGeneration = useCallback((jobId: string) => {
    removeJob(jobId);
  }, [removeJob]);

  // ============================================
  // RESTORE FROM SESSION STORAGE
  // ============================================

  useEffect(() => {
    const storedJobs = sessionStorage.getItem(STORAGE_KEY);
    if (storedJobs) {
      try {
        const jobsArray = JSON.parse(storedJobs) as QueueJob[];
        const restoredJobs = new Map<string, QueueJob>();

        jobsArray.forEach((job) => {
          // Only restore active jobs
          if (
            job.status === JobStatus.QUEUED ||
            job.status === JobStatus.IN_PROGRESS
          ) {
            restoredJobs.set(job.jobId, job);
          }
        });

        setJobs(restoredJobs);
      } catch (err) {
        console.error('Failed to restore queue jobs:', err);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    setInitialized(true);
  }, []);

  // ============================================
  // POLLING LOGIC
  // ============================================

  useEffect(() => {
    if (!initialized) return;

    jobs.forEach((job) => {
      const isActive =
        job.status === JobStatus.QUEUED || job.status === JobStatus.IN_PROGRESS;

      if (!isActive) {
        // Clear polling if job is complete
        const interval = pollIntervalsRef.current.get(job.jobId);
        if (interval) {
          clearInterval(interval);
          pollIntervalsRef.current.delete(job.jobId);
        }
        return;
      }

      // Start polling if not already polling
      if (!pollIntervalsRef.current.has(job.jobId)) {
        const pollInterval = job.pollInterval || 2000;

        const poll = async () => {
          try {
            // IMPORTANT: Get fresh job data from Map, not stale closure
            const currentJob = jobs.get(job.jobId);
            if (!currentJob) {
              console.log(`[POLL] Job ${job.jobId} no longer exists, stopping poll`);
              return;
            }

            if (currentJob.queueType === QueueType.PAGE_GENERATION) {
              const pageJob = currentJob as PageGenerationJob;
              const { getPageGenerationStatus } = await import('../services/pages');
              const result = await getPageGenerationStatus(
                pageJob.organisationId,
                pageJob.planId,
                pageJob.chapterId,
                pageJob.pageId
              );

              if (result.success && result.page) {
                const status = result.page.generationStatus;

                if (status === 'completed') {
                  completePageGeneration(pageJob.jobId, true);
                } else if (status === 'failed') {
                  completePageGeneration(pageJob.jobId, false, 'Page generation failed');
                } else if (status === 'generating') {
                  updateJob(pageJob.jobId, {
                    status: JobStatus.IN_PROGRESS,
                  });
                }
              }
            } else if (currentJob.queueType === QueueType.FLOWCHART_GENERATION) {
              const flowJob = currentJob as FlowchartGenerationJob;
              const { fetchFlowDiagram } = await import('../services/flowDiagram');

              try {
                const flowDiagram = await fetchFlowDiagram(
                  flowJob.organisationId,
                  flowJob.planId
                );

                if (flowDiagram) {
                  const status = flowDiagram.generationStatus;

                  if (status === 'completed') {
                    completeFlowchartGeneration(flowJob.jobId, true);
                  } else if (status === 'failed') {
                    completeFlowchartGeneration(flowJob.jobId, false, 'Flowchart generation failed');
                  } else if (status === 'generating') {
                    updateJob(flowJob.jobId, {
                      status: JobStatus.IN_PROGRESS,
                    } as Partial<FlowchartGenerationJob>);
                  }
                }
              } catch (error) {
                console.error('Error polling flowchart:', error);
              }
            }
          } catch (err) {
            console.error(`Error polling job ${job.jobId}:`, err);
          }
        };

        const intervalId = setInterval(poll, pollInterval);
        pollIntervalsRef.current.set(job.jobId, intervalId);

        // Call poll immediately instead of waiting for first interval
        poll();
      }
    });

    // Cleanup intervals for removed jobs
    pollIntervalsRef.current.forEach((interval, jobId) => {
      if (!jobs.has(jobId)) {
        clearInterval(interval);
        pollIntervalsRef.current.delete(jobId);
      }
    });
  }, [initialized, jobs, completePageGeneration, updateJob]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach((interval) => clearInterval(interval));
    };
  }, []);

  return (
    <QueueContext.Provider
      value={{
        jobs,
        addJob,
        updateJob,
        removeJob,
        pageGenerationJobs,
        planImportJob,
        flowchartGenerationJobs,
        startPageGeneration,
        completePageGeneration,
        clearPageGeneration,
        startFlowchartGeneration,
        completeFlowchartGeneration,
        clearFlowchartGeneration,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
};
